import Bugsnag, { NotifiableError } from '@bugsnag/js';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import {
  sendLegacyTerrainContact,
  SendLegacyTerrainContactPayload,
  sendLegacyTerrainShowPhoneContact,
  sendLegacyTerrainWhatsappContact,
  setIsLeadLimitReached,
  setStatus
} from '../slices/contactForm';
import { loadMultipleLeadsModal } from '../slices/multipleLeads';
import { closeModalForm, showPhone } from '../slices/showPhoneForm';
import { RootState } from '../store';
import legacyTerrainContactFormFetcher, {
  LegacyTerrainContactFormFetcherArgs
} from './fetchers/legacyTerrainContactFormFetcher';

type PartialValues = Pick<
  LegacyTerrainContactFormFetcherArgs,
  'email' | 'message' | 'name' | 'phone' | 'source'
>;

export function* sendLegacyTerrainContactFormWorker(
  action: PayloadAction<SendLegacyTerrainContactPayload>
) {
  const values: PartialValues = yield select((state: RootState) => ({
    email: state.user.email.value,
    message: state.contactForm.message,
    name: state.user.name.value,
    phone: state.user.phone.value,
    source: state.contactForm.source
  }));
  try {
    const response: Response = yield call(legacyTerrainContactFormFetcher, {
      ...values,
      code: action.payload.code,
      terrainId: action.payload.legacyTerrainId,
      terrainTitle: action.payload.legacyTerrainTitle,
      terrainUri: action.payload.legacyTerrainUri,
      recaptchaToken: action.payload.recaptchaToken,
      recaptchaAction: action.payload.recaptchaAction
    });

    if (!response.ok) {
      if (response.status === 429) {
        yield put(setIsLeadLimitReached(true));

        if (values.source === 'showPhone') {
          yield put(closeModalForm());
        }
      } else {
        yield put(setStatus('error'));
      }

      return;
    }

    if (window && window.dataLayer) {
      window.dataLayer.push({
        event: 'lead',
        marketplace: {
          category: action.payload.category,
          city: action.payload.city,
          country: process.env.NEXT_PUBLIC_COUNTRY,
          currency: action.payload.currency,
          item_id: action.payload.legacyTerrainId,
          operation: action.payload.operation,
          province: action.payload.province,
          region: action.payload.region,
          source: values.source,
          value: action.payload.value
        }
      });
    }

    if (values.source === 'showPhone') {
      yield put(showPhone());
    } else if (values.source === 'contactForm') {
      yield put(
        loadMultipleLeadsModal({
          email: values.email,
          propertyId: action.payload.legacyTerrainId,
          type: 'terrains'
        })
      );
    } else {
      yield put(setStatus('success'));
    }
  } catch (e) {
    Bugsnag.notify(e as NotifiableError);

    yield put(setStatus('error'));
    if (values.source === 'showPhone') {
      yield put(closeModalForm());
    }
  }
}

export default function* watchLegacyTerrainContactForm() {
  yield takeLatest(
    [
      sendLegacyTerrainContact,
      sendLegacyTerrainWhatsappContact,
      sendLegacyTerrainShowPhoneContact
    ],
    sendLegacyTerrainContactFormWorker
  );
}
